"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "motion/react";

import { usePanelScrollToElement } from "@/components/host-panel/shell/panel-layers";
import { SectionLabel } from "@/components/shared/section-label";
import { useToast } from "@/components/ui/toast";
import { loadAnalyticsVisitor } from "@/lib/analytics/analytics-user";
import { track } from "@/lib/analytics/data-layer";
import { completeOnboardingStep } from "@/lib/analytics/onboarding-funnel";
import { activeFilterGroups } from "@/lib/analytics/catalog-analytics";
import { pieceToAnalyticsItem } from "@/lib/analytics/ecommerce";
import { useAnalyticsListId } from "@/lib/analytics/list-context";
import { catalogSearchSubmitted, itemHighlighted } from "@/lib/host-panel/panel-analytics";
import {
  FEATURED_LIMIT,
  applyBulk,
  applyPack,
  removePiece,
  toggleFeatured,
  type BulkAction,
} from "@/lib/host-panel/catalog-mutations";
import type { PackForm } from "@/lib/host-panel/catalog-pack";
import {
  EMPTY_FACET_FILTERS,
  applyFacets,
  facetsFromCatalogQuery,
  hasActiveFacets,
  selectedValues,
  withSingleCollection,
  type FacetFilters,
} from "@/lib/host-panel/catalog-facets";
import {
  EMPTY_TAXONOMY,
  buildCatalogRails,
  buildServedCategoryRails,
  facetTypesInScope,
  hasTaxonomySelection,
  selectCategory,
  selectMacro,
  selectType,
  type TaxonomySelection,
} from "@/lib/host-panel/catalog-rails";
import {
  addFilterOptions,
  featuredCount,
  filterByScope,
  resolveAddFilter,
  scopeCounts,
} from "@/lib/host-panel/catalog-scope";
import type { MacroKey } from "@/lib/host-panel/catalog-taxonomy";
import { packTotalCents } from "@/lib/host-panel/catalog-types";
import type {
  AddFilter,
  CatalogFacets,
  CatalogPage,
  CatalogScope,
  ListedPiece,
} from "@/lib/host-panel/catalog-types";
import {
  CATALOG_DEFAULT_PAGE_SIZE,
  type CatalogPageSize,
  type CatalogQuery,
} from "@/lib/host-panel/catalog-query";
import {
  useHostCatalog,
  useStackedCatalogPages,
} from "@/lib/host-panel/use-host-catalog";
import { CatalogGridSkeleton } from "./catalog-grid-skeleton";
import { CatalogPagination } from "./catalog-pagination";
import { CatalogSearchField } from "./catalog-search-field";
import {
  catalogScopeToSearchParams,
  replacePanelQuery,
} from "@/lib/host-panel/panel-query";
import type { HostPresentation } from "@/lib/host-panel/presentation";
import { AddFilterPills } from "./add-filter-pills";
import { AdoptSuggestionLink } from "./adopt-suggestion-link";
import { BulkBar } from "./bulk-bar";
import { CatalogBrandQuote } from "./catalog-brand-quote";
import { CatalogEmpty } from "./catalog-empty";
import { CatalogError } from "./catalog-error";
import { CatalogHero, type HeroSuggestion } from "./catalog-hero";
import { CatalogInstructions } from "./catalog-instructions";
import { FacetRail } from "./facet-rail";
import { ModeFob } from "./mode-fob";
import { PackDialogLazy } from "./pack-dialog-lazy";
import { HOST_LIST_ITEMS_ENDPOINT, useListItemsWriter } from "./use-list-items-writer";
import { PieceCard } from "./piece-card";
import { TaxonomyRail } from "./taxonomy-rail";
import { catalogOrdersFor, orderPieces, type CatalogOrder } from "@/lib/host-panel/catalog-order";
import { matchesCatalogSearch } from "@/lib/host-panel/catalog-search";
import { useSkeletonPulse } from "@/lib/host-panel/use-skeleton-pulse";
import { CatalogToolbar, catalogCounter } from "./catalog-toolbar";
import {
  CREATE_LIST_MESSAGE,
  curationCountLabel,
} from "@/lib/create-list/create-list";
import { pieceDetailHref, withCatalogScope } from "@/lib/host-panel/piece-href";

/**
 * The catalog under management (handoff §7), management context only (criteria 17.1, 17.20).
 *
 * The pipeline is a sequence of pure functions — scope, then taxonomy, then facets, then grid —
 * and each stage **validates the choice against what exists** rather than correcting it in an
 * effect. That is what makes a ghost filter impossible without rewriting state on a timer.
 */

/**
 * The grid's heading, literal from the prototype — and **one** for both scopes: the prototype
 * keeps one heading ("Escolhas que contam uma história", no trailing
 * period) for the guest and the host's own list while the host adds pieces, and tells the add scope apart by the "O que
 * exibir" pills and the floating control. A second heading ("O catálogo inteiro, à sua escolha") is the prototype's internal curation context, and had been borrowed
 * for this scope.
 */
const LIST_TITLE = { lead: "Escolhas que contam uma", accent: "história" } as const;

/** The creation's curation heading, literal from the prototype (`verCuradoria`). */
const CURATION_TITLE = {
  eyebrow: "Monte a sua lista",
  lead: "O catálogo inteiro, à sua",
  accent: "escolha",
} as const;

/** A house suggestion list's grid heading, literal from the prototype (`verSugestao`). */
const SUGGESTION_TITLE = {
  eyebrow: "Curadoria da casa",
  lead: "As peças desta",
  accent: "lista",
} as const;

/** The suggestion's adopt CTA, the default `suggestion.adoptLabel`. */
const SUGGESTION_ADOPT_LABEL = "Quero essa lista";

export type CatalogSectionProps = {
  /**
   * The catalog with each piece's list state, rendered on the server.
   *
   * From here on mutations are local and optimistic, since persistence waits on the catalog
   * contract (task 21.3).
   */
  pieces: readonly ListedPiece[];
  /** Initial scope; `?modo=add` in the URL opens in add mode. */
  initialScope?: CatalogScope;
  /**
   * Target for a piece's detail, absent by default.
   *
   * The product detail page is out of scope for this delivery, and pointing at a route that
   * does not exist would ship a 404 dressed as a link. The seam stays for when it exists.
   */
  pieceHref?: (piece: ListedPiece) => string | null;
  /**
   * The same targets, as data instead of a function — what a Server Component can hand over
   * (the host's tab is one). Keyed by `ListedPiece.id`; a piece missing from the map has no
   * page and gets no link. `pieceHref`, when given, wins.
   */
  pieceHrefs?: Readonly<Record<string, string>>;
  /**
   * Hero above the grid (handoff §7): event name, cover, welcome message, date and location.
   *
   * Optional so the many catalog-behaviour tests that predate the hero (facets, packs, bulk
   * selection) do not need three unrelated props just to render. `panel.presentation` already
   * has a value at every real call site (`lista/page.tsx`).
   */
  eventName?: string;
  /** The hosts' first names, for the grid's eyebrow ("A lista de Maria e João"). */
  hostsShort?: string;
  presentation?: HostPresentation;
  isClosed?: boolean;
  /**
   * The catalog to explore, as the server answered it for the query in the URL (filters spec,
   * phase 1). Absent leaves the add scope on whatever `pieces` already carries.
   */
  catalog?: { page: CatalogPage; query: CatalogQuery };
  /**
   * Whether this list can be changed at all (`list-write-access.ts`). False takes every write
   * trigger off the screen — the hero's edit, the featured star, the pack dialog, the add scope
   * and the bulk bar — and leaves the reading of the list untouched.
   */
  canWrite?: boolean;
  /** Public list URL, for the Controle_de_Modo's "Copiar link" (requirement 1.11). */
  publicListUrl?: string;
  /**
   * Who is looking. `host` (default) is the panel's tab, where the guest's view is a preview the
   * host enters and leaves. `guest` is the public page of the list: that same view and nothing
   * else — no floating mode control, no add scope, no pack dialog, no editing, no write at all.
   * `suggestion` is one of the house's ready-made lists: read-only like the guest's page, with no
   * price, no gift to give, and an invitation to start one's own list from it.
   */
  audience?: "host" | "guest" | "suggestion" | "curation";
  /**
   * The creation's curation (`curation`): the whole catalog, each piece in or out of the list with
   * one press, written at once; the bar counts what was chosen and "Continuar" hands over.
   */
  /** Receives how many pieces were chosen (the prototype's "N peças salvas na sua lista"). */
  onFinishCuration?: (chosenCount: number) => void;
  /**
   * Hands the curation's save to whoever owns the step's exits.
   *
   * The curation does not write press by press (see `commit`), so every way out of the step has to
   * be able to write first — "Salvar e sair" most of all, since it logs out and its label promises
   * otherwise. `save` resolves `false` when the write was refused, and `hasUnsavedChanges` is what
   * arms the browser's own warning before the page is abandoned.
   */
  onProvideWriter?: (writer: {
    save: () => Promise<boolean>;
    hasUnsavedChanges: () => boolean;
  }) => void;
  isFinishingCuration?: boolean;
  /**
   * The suggestion list's hero, where its adopt CTA leads, and the CTA's words (`adoptLabel`,
   * default "Quero essa lista"). Read only for `suggestion`.
   */
  suggestion?: HeroSuggestion & {
    adoptHref: string;
    adoptLabel?: string;
    /** What the adoption event reports (`suggestion_list_adopted`); absent, nothing is reported. */
    analytics?: { suggestionId: string; itemsCount: number };
  };
  /**
   * The BFF route every write of the list's pieces goes to (`PATCH`, the batch body of
   * `PATCH /gift-lists/{id}/items`). Default: the host's, `/api/host/list-items`. Another flow
   * (the internal list creation) writes the same batch through its own route.
   */
  itemsEndpoint?: string;
  /**
   * The BFF route the catalog to explore is paged from, after the server's first page. Default
   * (`undefined`): the host's, `HOST_CATALOG_ENDPOINT` of `use-host-catalog`.
   */
  catalogEndpoint?: string;
  /**
   * The search field's words. Default (`undefined`): the host's, by name or collection. The
   * internal area says SKU (§6.2), which is how the staff look a piece up.
   */
  searchPlaceholder?: string;
  /**
   * What "Presentear" does for a real guest — **the seam where the cart plugs in**. Absent, the
   * action answers as the prototype's own `presentear()` does: a confirmation by toast, no
   * request and no navigation. Read only when `audience` is `guest`; the host's preview stays
   * decorative by design.
   */
  onPresent?: (piece: ListedPiece) => void;
  /** The cart's own busy state, for the guest's action (`public-list-storefront`). */
  presentingPieceId?: string | null;
  isPresentingAny?: boolean;
  /**
   * The host is the one looking at their own public list. Nobody buys a gift for themselves, so
   * the card's action becomes the design's locked row instead of "Presentear"; the pieces are
   * managed in the panel, which is where the triggers live.
   */
  isOwnList?: boolean;
  /** The hero's desktop height, for a page with a header above it (`CatalogHero`'s `height`). */
  heroHeight?: string;
};


const EMPTY_PRESENTATION: HostPresentation = {
  coverKey: "",
  message: "",
  coverOptions: [],
  eventDate: "",
  eventLocation: "",
};

export function CatalogSection({
  pieces: initialPieces,
  initialScope = "dentro",
  pieceHrefs,
  /*
   * The prototype's card opens the piece's page (`abrirPdp`). With `pieceHrefs` the page says
   * where each piece lives; without it, the prototype's own route, `/produtos/[id]`.
   *
   * The scope is restated here rather than taken from the server's link, because the floating
   * control changes scope on the client: without this, leaving a piece opened from Escopo_Fora
   * put the host back in Escopo_Dentro (`withCatalogScope`).
   */
  pieceHref,
  eventName = "",
  hostsShort = "",
  presentation = EMPTY_PRESENTATION,
  isClosed = false,
  canWrite = true,
  catalog,
  publicListUrl,
  audience = "host",
  onPresent,
  presentingPieceId = null,
  isPresentingAny = false,
  isOwnList = false,
  heroHeight,
  suggestion,
  onFinishCuration,
  onProvideWriter,
  isFinishingCuration = false,
  itemsEndpoint = HOST_LIST_ITEMS_ENDPOINT,
  catalogEndpoint,
  searchPlaceholder,
}: CatalogSectionProps) {
  const isGuestPage = audience === "guest";
  // The list the page publishes (INST §03: `list_id` on every event born inside a list, navigation included).
  const analyticsListId = useAnalyticsListId();
  const listIdParam = analyticsListId === null ? {} : { list_id: analyticsListId };
  const isSuggestion = audience === "suggestion";
  const isCuration = audience === "curation";
  /** A page nobody manages from: the guest's list, or a house suggestion. */
  const isReadOnlyPage = isGuestPage || isSuggestion;
  /** The host's panel tab is the only place with the mode control, the bulk bar and packs. */
  const isPanelTab = audience === "host";
  const { showToast } = useToast();
  const [packPiece, setPackPiece] = useState<ListedPiece | null>(null);
  const packTrigger = useRef<HTMLElement | null>(null);
  /** The top of the list — eyebrow, title and filters — where a change of view lands. */
  /**
   * The list-items engine (`use-list-items-writer.ts`): the optimistic series, the write chain
   * that coalesces presses, the rollback and every message.
   *
   * `deferWrites` is the one difference between the two screens: Minha Lista writes as it goes,
   * and the **curation writes when the step is submitted** (the person's decision of
   * 2026-10-06) — choosing pieces is one act with one result, so it travels as one request, at
   * the moment the person says they are done.
   */
  const { pieces, commit, save: scheduleWrite, savedPieces, isSaving } = useListItemsWriter({
    initialPieces,
    itemsEndpoint,
    canWrite,
    deferWrites: isCuration,
    onProvideWriter,
  });

  /** A departure already waiting on the write chain; a second press must not start another. */
  const isHandingOver = useRef(false);
  /**
   * The card's target, with the scope the person is actually looking at.
   *
   * A caller's own `pieceHref` wins — the house suggestion lists pass one that returns `null`,
   * because the design's products are not the backend's and there is no page to open. Otherwise
   * the page's map answers, and a piece the map does not know (the guest's preview, a served
   * catalog page) still opens its own product page.
   */
  function resolvePieceHref(piece: ListedPiece): string | null {
    if (pieceHref !== undefined) return pieceHref(piece);

    const href = pieceHrefs?.[piece.id] ?? pieceDetailHref(piece.id);

    return withCatalogScope(href, scope);
  }

  const listTopRef = useRef<HTMLDivElement>(null);
  const scrollPanelTo = usePanelScrollToElement();
  /** Bumped by the mode control; the glide runs once the new view has been laid out. */
  const [viewChange, setViewChange] = useState(0);
  // `?modo=add` cannot open the add scope on a list nothing can be added to.
  // The curation is the catalog to explore and nothing else.
  const [scope, setScope] = useState<CatalogScope>(
    isCuration ? "fora" : canWrite ? initialScope : "dentro",
  );
  /** The applied term (§3: typing does not filter), the page, and how many rows it holds. */
  const [appliedSearch, setAppliedSearch] = useState(
    catalog?.query.search ?? "",
  );
  /**
   * Bumped whenever the search must be wiped from outside the field. The field keeps the term
   * being typed in its own state and only re-syncs when `applied` changes; with `applied`
   * already empty, an unsent term would survive a reset. A new `key` remounts the field instead.
   */
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [page, setPage] = useState(catalog?.query.page ?? 1);
  const [pageSize, setPageSize] = useState<CatalogPageSize>(
    catalog?.query.pageSize ?? CATALOG_DEFAULT_PAGE_SIZE,
  );
  /** True once "Carregar mais" was used: the grid grows instead of turning the page (§7). */
  const [isStacking, setIsStacking] = useState(false);
  const [requestedAddFilter, setRequestedAddFilter] =
    useState<AddFilter>("todos");
  const [taxonomy, setTaxonomy] = useState<TaxonomySelection>(EMPTY_TAXONOMY);
  /**
   * The marks the address already carries, so a shared or bookmarked filtered list opens
   * filtered — and the page the server rendered for that same query is the one the client keeps
   * (`use-host-catalog` seeds its cache with it).
   */
  const [facets, setFacets] = useState<FacetFilters>(() =>
    catalog === undefined
      ? EMPTY_FACET_FILTERS
      : facetsFromCatalogQuery(catalog.query),
  );
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  /**
   * Guest preview (requirement 1.16, `logic/01.js:188`), restricted to `dentro`: a flag beside
   * `scope`, not a third `CatalogScope` value, since leaving the scope is real navigation and
   * this only changes what the same grid renders.
   */
  const [isPreviewing, setIsGuestPreview] = useState(false);
  // On the guest's own page the guest's view is not a mode: it is the page.
  const isGuestPreview = isGuestPage || isPreviewing;
  /** Which context the instructions and the order control read (canon §10). */
  const orderAudience = isSuggestion ? "sugestao" : isCuration ? "curadoria" : isGuestPreview ? "convidado" : "gestao";

  const options = useMemo(() => addFilterOptions(pieces), [pieces]);
  const addFilter = resolveAddFilter(pieces, requestedAddFilter);
  const counts = useMemo(() => scopeCounts(pieces), [pieces]);

  const inScope = useMemo(
    () => filterByScope(pieces, scope, addFilter),
    [pieces, scope, addFilter],
  );

  /**
   * Where the catalog to explore comes from: the server, whenever the page handed one over.
   * The list scope never queries — the list's own pieces are already here.
   */
  const isAddScope = scope === "fora";
  const isServerCatalog = catalog !== undefined && isAddScope;

  /**
   * The categories the server has counted, **the last complete list it gave us**.
   *
   * A product of `GET /catalog/products` carries no taxonomy (`docs/backend-requests.md`, item 12), but
   * the response carries the aggregate: 95 categories with their counts. It is the one source of
   * the trail over the served catalogue. Once a category is chosen the backend answers without
   * `categories` (measured 2026-10-14), so the list is kept from the page that had it — a rail
   * that vanished on selection would take the only way back with it.
   */
  const [knownCategories, setKnownCategories] = useState(
    catalog?.page.facets.categories ?? [],
  );
  /**
   * The collections the server has counted, **the last complete list it gave us** — the second
   * line under the "Coleções" department. Choosing one makes the backend answer with neither
   * `collections` nor `categories` (measured: `catalog-search.service.ts` answers `kits` alone
   * under a `collectionId`), and choosing a category answers with the collections **of that
   * category** only: either way the page in hand is not the list, so the one kept is the page
   * that brought the whole of it.
   */
  const [knownCollections, setKnownCollections] = useState(
    catalog?.page.facets.collections ?? [],
  );

  /**
   * In hand, the tracks come from the pieces. Over the served catalogue they come from the
   * aggregates (`buildServedCategoryRails`): two departments, "Todas as peças" and "Coleções" —
   * the eight merchandising ones wait for the map that nobody has authored yet
   * (`docs/internal/proposta-mapa-categorias-departamentos.md`).
   */
  const rails = useMemo(
    () =>
      isServerCatalog
        ? buildServedCategoryRails(knownCategories, taxonomy, [], knownCollections)
        : buildCatalogRails(inScope, taxonomy),
    [isServerCatalog, knownCategories, knownCollections, inScope, taxonomy],
  );
  // The prototype opens on the curation order — featured first — and lets the person re-order,
  // unless the address already named one.
  const [requestedOrder, setOrder] = useState<CatalogOrder>(
    catalog?.query.order ?? "curadoria",
  );
  // An order the audience is not offered falls back to the curation's, derived rather than
  // rewritten: "Mais presenteados" reads the host's own numbers (not for a guest), and price is
  // not offered where the context shows none (handoff §6) — which the address may still name.
  const order: CatalogOrder = catalogOrdersFor(orderAudience).some((option) => option.key === requestedOrder)
    ? requestedOrder
    : "curadoria";
  /**
   * The applied search narrows a list in hand right here (`casaQ`); over the served catalogue it
   * travels with the query, and this slice is not the one drawn.
   */
  const visible = useMemo(
    () =>
      orderPieces(
        applyFacets(rails.scoped, facets).filter((piece) => matchesCatalogSearch(piece, appliedSearch)),
        order,
      ),
    [rails.scoped, facets, order, appliedSearch],
  );
  /** A list re-sliced in hand declares its wait like the served catalogue does (`pulsarCarga`). */
  const [isPulsing, pulseSkeleton] = useSkeletonPulse();
  /** The phone's attribute sheet covers the floating mode control, which steps aside meanwhile. */
  const [isFacetSheetOpen, setIsFacetSheetOpen] = useState(false);

  /** The whole cascade as one query (§2): category, marks, price, order, search and page. */
  /*
   * What the second line holds travels as what it is: a category as `categoryId`, and under the
   * "Coleções" department a collection as `collectionId`. Under that department the attribute
   * chip "Coleção" is not drawn and the marks were cleared with the department, so the line is
   * the only source of the collection.
   */
  const isServedCollections = isServerCatalog && rails.selection.macro === "colecoes";
  const selectedCategoryId = isServerCatalog && !isServedCollections ? rails.selection.category : null;
  const selectedCollectionId = isServedCollections ? rails.selection.category : null;
  const catalogQuery: CatalogQuery = useMemo(
    () => ({
      search: appliedSearch,
      categoryId: selectedCategoryId,
      trailType: selectedCategoryId === null ? null : taxonomy.type,
      collectionId: selectedCollectionId ?? selectedValues(facets.selection, "colecao")[0] ?? null,
      types: selectedValues(facets.selection, "tipo"),
      materials: selectedValues(facets.selection, "material"),
      priceMinCents: facets.price.minCents,
      priceMaxCents: facets.price.maxCents,
      order,
      page,
      pageSize,
    }),
    [appliedSearch, selectedCategoryId, selectedCollectionId, taxonomy.type, facets, order, page, pageSize],
  );

  const catalogRequest = useHostCatalog({
    query: catalogQuery,
    /*
     * The page the page handed over, or **nothing** — never an empty one standing in for it.
     * This used to substitute `EMPTY_CATALOG_PAGE`, so a screen that reads no catalogue at all
     * (a suggestion's preview, a guest's page) seeded emptiness into the cache under the key
     * the panel was about to use, and the panel's real page was then ignored.
     */
    initial: catalog,
    enabled: isServerCatalog,
    endpoint: catalogEndpoint,
  });

  // Adopt the complete list when a page brings one (adjusting state during render, not in an
  // effect). A page scoped to a category brings none and leaves what we know untouched.
  const servedCategories = catalogRequest.page.facets.categories;
  if (servedCategories.length > 0 && servedCategories !== knownCategories) {
    setKnownCategories(servedCategories);
  }

  /**
   * The types of the chosen category, for the N3 line (handoff §2 item 6).
   *
   * They come from the page's `tipo-de-produto` aggregate, which the backend scopes to the
   * category (BANDEJA: one value; the whole catalogue: 95). A page still on its way would hand
   * over the previous scope's types, and picking a type sends a new query whose page is not here
   * yet — so what is drawn is the list **read for this category**, kept until the category
   * changes. The line does not blink out while the grid waits.
   */
  const [typeScope, setTypeScope] = useState<{
    categoryId: string | null;
    options: CatalogFacets["types"];
  }>({ categoryId: null, options: [] });
  const pageTypes = catalogRequest.page.facets.types;
  const isPageSettled = !catalogRequest.isLoading && !catalogRequest.isRefreshing && !catalogRequest.isError;
  /*
   * Every number read off the page's totals waits for the page that is **this slice's**. Until it
   * arrives the hook hands back the page the server rendered — the whole catalogue, unfiltered —
   * so a counter drawn from it read "732 peças" under a category of twenty-two and then corrected
   * itself (reported 2026-10-22). A refetch of a slice already held is not this: its page is the
   * slice's own and its numbers are right, so only a slice with nothing yet pends.
   */
  const isCountPending = isServerCatalog && catalogRequest.isLoading;

  // Adopt the complete list of collections, from a settled page that is **not** scoped to a
  // category: that one brings the category's own collections, a subset, and a page under a
  // collection brings none. Adjusting state during render, as the categories above.
  const servedCollections = catalogRequest.page.facets.collections;
  if (
    isServerCatalog &&
    isPageSettled &&
    selectedCategoryId === null &&
    servedCollections.length > 0 &&
    servedCollections !== knownCollections
  ) {
    setKnownCollections(servedCollections);
  }
  if (
    isServerCatalog &&
    isPageSettled &&
    selectedCategoryId !== null &&
    (typeScope.categoryId !== selectedCategoryId || typeScope.options !== pageTypes)
  ) {
    setTypeScope({ categoryId: selectedCategoryId, options: pageTypes });
  }
  const typesInScope = useMemo(
    () => (typeScope.categoryId === selectedCategoryId ? typeScope.options : []),
    [typeScope, selectedCategoryId],
  );

  /** The trail as drawn: the categories, and below a category its types. */
  const trail = useMemo(
    () =>
      isServerCatalog
        ? buildServedCategoryRails(knownCategories, taxonomy, typesInScope, knownCollections)
        : rails,
    [isServerCatalog, knownCategories, knownCollections, taxonomy, typesInScope, rails],
  );

  /** The facets of the page in hand: "Tipo" without the category's own name or the trail's choice. */
  const liveRailFacets = useMemo(() => {
    const categoryName = knownCategories.find((entry) => entry.value === selectedCategoryId)?.label ?? null;

    return {
      ...catalogRequest.page.facets,
      types: facetTypesInScope(catalogRequest.page.facets.types, categoryName, trail.selection.type),
    };
  }, [catalogRequest.page.facets, knownCategories, selectedCategoryId, trail.selection.type]);

  /**
   * The facets the rail draws: **the last settled page's**, held while the next one is on its way.
   *
   * While a new slice's query is pending, `useHostCatalog` hands back the page the server rendered
   * — unfiltered, with every facet populated — and drawing the rail from it painted all four
   * attribute chips for the length of the round trip, then dropped the ones the new slice does
   * not have (reported 2026-10-21, "Adicionar peças"). The grid never does this: it holds the
   * previous page and declares the wait in skeleton. The rail follows the same discipline, and
   * the same one as `typeScope` above: the line does not blink while the grid waits.
   *
   * The first page is the server's own and arrives settled (it seeds the cache), so it is drawn
   * from the start; what must never happen is that page standing in **after** a real one was read.
   * Adjusting state during render, not in an effect: there is no frame with the stale set.
   */
  const [heldRailFacets, setHeldRailFacets] = useState(liveRailFacets);
  // A failed read is not a wait: it ends the hold and the rail follows the (empty) page, as it
  // always did, so no chip is left claiming a slice that failed to load (`CatalogError` speaks).
  if ((isPageSettled || catalogRequest.isError) && heldRailFacets !== liveRailFacets) {
    setHeldRailFacets(liveRailFacets);
  }
  const railFacets = heldRailFacets;

  // Pages already read stay in the cache, so "Carregar mais" concatenates without a state of
  // its own — and turning the page on a wide screen replaces, as the canon draws it.
  const stackedItems = useStackedCatalogPages({
    query: catalogQuery,
    page: catalogRequest.page,
    isStacking: isStacking && isServerCatalog,
  });

  /**
   * What the grid draws: the server's page in the add scope, the list's own slice otherwise.
   *
   * A piece the person just added is already in `pieces`, with its arrangement and its goal, so
   * it wins over the catalog's copy — the card flips to "Editar" without waiting for a refetch.
   */
  const shown = useMemo(
    () => {
      if (!isServerCatalog) return visible;

      // A marked piece stays exactly where the server put it (ruling of the team: the lifting
      // rule is revoked). What it keeps from the list is the fresher data: same position, but
      // the list's copy wins, so the card shows "Editar" without waiting for a refetch.
      // A piece on the list that is not on the server's page has no position here and does not
      // appear in the add scope — it already has its own tab.
      const inList = new Map(pieces.map((piece) => [piece.id, piece]));

      return stackedItems.map((item) => inList.get(item.id) ?? (item as ListedPiece));
    },
    [isServerCatalog, stackedItems, pieces, visible],
  );
  const shownCount = isServerCatalog ? stackedItems.length : shown.length;
  const scopeTotal = isServerCatalog
    ? catalogRequest.page.totalItems
    : rails.scoped.length;
  const scopeCountsForFob =
    catalog === undefined
      ? counts
      : { ...counts, fora: catalog.page.totalItems };

  /** Every change but the page itself goes back to the first one (§2's reset cascade). */
  function restart() {
    setPage(1);
    setIsStacking(false);
  }

  /**
   * The bulk selection dies with the slice it was made on (product owner, 2026-10-21): **if a
   * product that was on the grid can leave it, the selection is zeroed** — the same for every
   * control, no exception by kind. Otherwise the bar keeps counting pieces nobody sees any more.
   *
   * This is `restart()` plus the selection, and the choice between the two is the rule's two
   * exclusions, made at each call site instead of an `if` here:
   *
   * - **Sorting** (`changeOrder`) calls plain `restart()`: it is the same set in another order,
   *   no selected piece leaves the grid, it only moves. Zeroing would destroy work for nothing.
   * - **"Carregar mais"** (`onLoadMore`) touches neither: it concatenates, the set only grows.
   *
   * Turning the page (`onPageChange`) clears the selection without passing through here, since it
   * keeps the page it is turning to; `restart()` alone would have left it out.
   *
   * Plain state setters called from one event handler, never an effect: React batches them with
   * the change itself into one commit, so there is no frame with the new slice and the old marks.
   */
  function leaveSlice() {
    restart();
    setSelection(new Set());
  }

  function changeFilters(next: FacetFilters) {
    // The backend takes one `collectionId`, so the axis behaves like one choice: marking a
    // collection replaces the previous one instead of silently dropping it on the wire.
    const applied = isServerCatalog ? withSingleCollection(facets, next) : next;
    if (isGuestPage) track({ event: "filter_applied", item_list_name: "catalog_guest", filters_active: activeFilterGroups(applied), ...listIdParam });
    setFacets(applied);
    leaveSlice();
  }

  function changeOrder(next: CatalogOrder) {
    if (isGuestPage) track({ event: "sort_changed", item_list_name: "catalog_guest", sort: next, ...listIdParam });
    setOrder(next);
    // Not `leaveSlice()`: same set, other order — the selection survives (see `leaveSlice`).
    restart();
  }

  function applySearch(term: string) {
    setAppliedSearch(term);
    leaveSlice();
    if (!isServerCatalog) pulseSkeleton();
  }

  /**
   * Where the search was made. The add scope's search reads the served catalogue; Minha Lista's
   * reads the list's own pieces, counted here. The guest's page and a house suggestion filter
   * their own pieces too, and are not the host's to report.
   */
  const searchContext = isAddScope ? "host_panel_add_mode" : "host_panel_list";
  const reportsSearch = isAddScope || isPanelTab;

  const lastTrackedSearch = useRef("");
  useEffect(() => {
    // Only track when the search is active and data is loaded.
    // Only the served catalogue waits on a read: Minha Lista filters the pieces it already holds
    // (the request is disabled there, and a disabled query reads as "pending" forever).
    const isFetching = isServerCatalog && (catalogRequest.isLoading || catalogRequest.isRefreshing);
    if (
      reportsSearch &&
      appliedSearch !== "" &&
      !isFetching &&
      appliedSearch !== lastTrackedSearch.current
    ) {
      track(
        catalogSearchSubmitted(
          searchContext,
          isServerCatalog ? catalogRequest.page.totalItems : visible.length,
        ),
      );
      lastTrackedSearch.current = appliedSearch;
    } else if (appliedSearch === "") {
      lastTrackedSearch.current = "";
    }
  }, [
    appliedSearch,
    catalogRequest.isLoading,
    catalogRequest.isRefreshing,
    catalogRequest.page.totalItems,
    reportsSearch,
    searchContext,
    isServerCatalog,
    visible.length,
  ]);

  const isFiltering =
    hasActiveFacets(facets) ||
    hasTaxonomySelection(trail.selection) ||
    appliedSearch !== "" ||
    (isAddScope && addFilter !== "todos");

  /*
   * `view_item_list`: the guest's grid, painted — once per distinct set of pieces on screen, never
   * while the page is still being read. The key is the ids in order, so a re-render that changes
   * nothing sends nothing, and a new page, filter or order (a different set) sends again.
   */
  const lastReportedGrid = useRef("");
  const isGridReady = isServerCatalog ? isPageSettled : !isPulsing;
  useEffect(() => {
    if (!isGuestPage || !isGridReady || shown.length === 0) return;
    // The grid is told with the list it belongs to: the page publishes it in an effect of its own, so wait for it.
    if (analyticsListId === null) return;

    const key = shown.map((piece) => piece.id).join(",");
    if (key === lastReportedGrid.current) return;
    lastReportedGrid.current = key;

    track({
      event: "view_item_list",
      item_list_name: "catalog_guest",
      filter_active: isFiltering,
      sort: requestedOrder,
      list_id: analyticsListId,
      ecommerce: {
        currency: "BRL",
        items: shown.map((piece, index) => pieceToAnalyticsItem(piece, { index, listName: "catalog_guest" })),
      },
    });
  }, [isGuestPage, isGridReady, shown, isFiltering, requestedOrder, analyticsListId]);


  /**
   * "Limpar filtros" (handoff §2, reset table): the marks, the featured toggle, the price range,
   * the typed and applied search, the add-mode filter ("Todos | Na lista | Fora dela"), the bulk
   * selection and the page. **Not** the department or the category — they are navigation, not
   * filter, and `facet-rail.tsx` has always said so.
   *
   * The bulk selection goes on purpose (product owner, 2026-10-21), through `clearRefinements` →
   * `leaveSlice`, which is where every change of slice clears it: keeping it would mean a bar
   * counting pieces the new slice no longer carries.
   */
  function clearRecorte() {
    setRequestedAddFilter("todos");
    clearRefinements();
  }

  /**
   * What every reset shares: marks, featured toggle, price range, the applied **and** the typed
   * search, the bulk selection (via `leaveSlice`), and the page back to 1.
   *
   * PRODUCT RULE, wider than the handoff: "everything that resets a filter resets the search too"
   * (product owner, 2026-10-21). Handoff §4 line 53 lists what changing the department clears and
   * does not mention the search; it is extended here, and to the category as well. The search
   * narrows the grid like any mark, and an empty grid behind a term the person no longer sees in
   * the slice they just opened would read as a broken screen.
   *
   * It is plain state setters called from one event handler, never an effect, so React batches
   * them with the navigation itself into a single commit: there is no frame with the new slice
   * and the old marks, or the other way round.
   */
  function clearRefinements() {
    setFacets(EMPTY_FACET_FILTERS);
    setAppliedSearch("");
    setSearchResetKey((key) => key + 1);
    leaveSlice();
  }

  /** "Trocar de contexto | tudo" (handoff §2): the recorte, and the navigation with it. */
  function resetEverything() {
    clearRecorte();
    setTaxonomy(EMPTY_TAXONOMY);
  }

  function changeScope(next: CatalogScope) {
    setScope(next);
    resetEverything();
    // Mode is the only catalog slice the URL keeps (requirement 1.7), because it is where the
    // "add pieces" shortcuts land. Facets and taxonomy reset on a mode change anyway.
    replacePanelQuery(catalogScopeToSearchParams(next));
  }

  // A new view opens at the top of the list, on every device: the grid under it is another one
  // now, and staying where the old one was read would land mid-list. After the commit, so the
  // guest preview's own instruction card above the list is already in place when it is measured.
  useEffect(() => {
    if (viewChange === 0 || listTopRef.current === null) return;
    scrollPanelTo(listTopRef.current);
  }, [viewChange, scrollPanelTo]);

  function changeViewFromFob(next: CatalogScope) {
    if (next !== scope || isGuestPreview) setViewChange((count) => count + 1);
    changeScope(next);
  }

  function toggleGuestPreviewFromFob(next: boolean) {
    if (next !== isGuestPreview) setViewChange((count) => count + 1);
    toggleGuestPreview(next);
  }

  function toggleGuestPreview(next: boolean) {
    setIsGuestPreview(next);
    // The reference's own `setModo` (`logic/01.js:409-413`): entering or leaving the preview
    // forces `dentro` and clears the recorte, the same reset a real scope change gets.
    setScope("dentro");
    resetEverything();
  }

  function presentAsGuest(piece: ListedPiece) {
    // A real guest's press goes to whoever plugged the cart in.
    if (isGuestPage && onPresent !== undefined) {
      onPresent(piece);
      return;
    }

    // `presentear()` (`logic/01.js:609-621`): a decorative confirmation, no request, no
    // pedido, no navigation — the preview is local and stays local.
    showToast(`${piece.name} vira crédito para os anfitriões`);
  }

  function toggleSelection(piece: ListedPiece) {
    setSelection((current) => {
      const next = new Set(current);
      if (next.has(piece.id)) next.delete(piece.id);
      else next.add(piece.id);

      return next;
    });
  }


  /**
   * The set a mutation runs over.
   *
   * The list's own pieces are the state this component holds; a piece the catalog served is not
   * in it yet, and looking it up there would answer "no such piece" and reject the change in
   * silence — which is exactly what "Adicionar" did on the served catalogue until 2026-09-24.
   */
  function withShown(...shownPieces: readonly ListedPiece[]): ListedPiece[] {
    const known = new Set(pieces.map((piece) => piece.id));

    return [...pieces, ...shownPieces.filter((piece) => !known.has(piece.id))];
  }

  /**
   * A piece put in or taken out of the list, for the analytics: its id, its pack's total in reais
   * and how many the list holds afterwards. `search` when a search term was applied to the catalog
   * it came from; the pre-marked pieces of an adopted suggestion are not a press, so not here.
   */
  function reportCurationItem(piece: ListedPiece, isAdding: boolean): void {
    track({
      event: isAdding ? "curation_item_added" : "curation_item_removed",
      item_id: piece.id,
      value: packTotalCents(piece) / 100,
      source: (catalog?.query.search ?? "") === "" ? "curation" : "search",
      items_count_after: Math.max(0, pieces.filter((candidate) => candidate.inList).length + (isAdding ? 1 : -1)),
    });
  }

  function savePack(piece: ListedPiece, form: PackForm) {
    if (!piece.inList) reportCurationItem(piece, true);
    commit(applyPack(withShown(piece), piece.id, form));
    setPackPiece(null);
  }

  function removeFromList(piece: ListedPiece) {
    if (piece.inList) reportCurationItem(piece, false);
    commit(removePiece(withShown(piece), piece.id));
    setPackPiece(null);
  }

  function applyBulkAction(action: BulkAction) {
    commit(applyBulk(withShown(...shown), selection, action));
    setSelection(new Set());
  }

  const title = isSuggestion
    ? SUGGESTION_TITLE
    : isCuration
      ? CURATION_TITLE
      : LIST_TITLE;
  const eyebrow = isSuggestion
    ? SUGGESTION_TITLE.eyebrow
    : isCuration
      ? CURATION_TITLE.eyebrow
      : hostsShort === ""
        ? ""
        : `A lista de ${hostsShort}`;
  const chosenCount = pieces.filter((piece) => piece.inList).length;

  /** One press, one piece in or out, written at once (`alternarSel`). */
  function toggleInCuration(piece: ListedPiece) {
    const isAdding = !piece.inList;
    const result = applyBulk(
      withShown(piece),
      new Set([piece.id]),
      isAdding ? "add" : "remove",
    );

    if (!result.isRejected) reportCurationItem(piece, isAdding);

    commit({
      ...result,
      message:
        isAdding && !result.isRejected
          ? `${piece.name} entrou na sua lista`
          : null,
    });
  }

  /**
   * Leaves the curation **only once the list really holds what the grid shows**.
   *
   * It used to read the local count and go. With a write that had failed, that count was the only
   * place the pieces existed: the step announced "N peças salvas na sua lista", the panel then
   * read a draft with no piece, and `(painel)/layout.tsx` sent the person straight back here —
   * a loop with no way out, on a list that was never saved (reported 2026-10-06).
   *
   * So it waits for its turn in the write chain. Whatever is pending goes up first; a failure has
   * already spoken for itself, and the step stays where it is, with the grid back on what the list
   * actually holds.
   */
  async function finishCuration() {
    if (chosenCount === 0) {
      showToast(CREATE_LIST_MESSAGE.emptyCuration, { variant: "error" });
      return;
    }
    if (isFinishingCuration || isHandingOver.current) return;

    isHandingOver.current = true;
    try {
      if (!(await scheduleWrite())) return;

      // The grid may have been rolled back to a list with nothing on it while we waited, so
      // this counts what the list is **known** to hold, not what the grid is showing.
      const saved = savedPieces().filter((piece) => piece.inList).length;
      if (saved === 0) {
        showToast(CREATE_LIST_MESSAGE.emptyCuration, { variant: "error" });
        return;
      }

      onFinishCuration?.(saved);
    } finally {
      // Released either way: a refused write has to leave the step pressable again.
      isHandingOver.current = false;
    }
  }

  return (
    // The suggestion's sticky bar closes the page itself: padding under it would leave it floating.
    <section
      className={
        isSuggestion || isCuration ? undefined : "pb-[clamp(52px,7vw,96px)]"
      }
    >
      {/* The curation has no hero (canon §10): the list is still being born. */}
      {isCuration ? null : (
        <CatalogHero
          eventName={eventName}
          presentation={presentation}
          isClosed={isClosed}
          suggestion={suggestion}
          height={heroHeight}
        />
      )}

      {/*
        The prototype reads each audience its own card: the host's single instruction, or the
        guest's three numbered steps in the preview.

        A closed list reads none. Every word of those cards is an instruction for a list that
        still takes gifts ("Adicione o que você quer receber, defina quantas unidades e quais
        pacotes" / "Escolha um presente"), and none of it is possible any more. The
        prototype keeps the card in this state — its `passos` section has no lifecycle binding
        (`Catalogo.dc.html:355`) — and the ruling of 2026-10-06 removes it; the hero's own
        notice ("Esta lista foi encerrada") is what speaks here.
      */}
      {isClosed ? null : <CatalogInstructions audience={orderAudience} />}

      {/*
       * Full-bleed like the hero and the brand quote (`template.html:378-478`): `width:
       * var(--vpW)` breaks the header, filters and grid out of the shell's 1280px container, so
       * on wide viewports this block spans nearly the full window rather than the shell's cap.
       * The sticky counter and order bar carries its own horizontal inset (`PlpFilterBar`), so it
       * sits outside the padded wrapper to keep its backdrop blur edge-to-edge.
       */}
      {/* The prototype's catalog section closes with its own breath — clamp(52px, 6vw, 80px) —
          before the brand quote opens with the same measure. */}
      <div ref={listTopRef} className="w-screen pb-[clamp(52px,6vw,80px)] [margin-inline:calc(50%-50vw)]">
        <div className="px-[clamp(20px,4vw,56px)] pt-[clamp(34px,4vw,48px)]">
          {/* "A lista de Maria e João": the prototype names the hosts over their own list. With no
              name to write, the eyebrow is not rendered — no neutral copy stands in for it. */}
          {eyebrow === "" ? null : (
            <SectionLabel tone="brand" rule={false} className="mb-0">
              {eyebrow}
            </SectionLabel>
          )}

          <h2 className="mb-[clamp(26px,3.2vw,40px)] mt-[30px] font-display text-[clamp(26px,3.17vw,42px)] font-light uppercase leading-[1.06] tracking-[-0.01em]">
            {title.lead} <em className="text-tb-green">{title.accent}</em>
            {title === LIST_TITLE ? null : "."}
          </h2>

          {/*
            The filter block (handoff v2 §6.2): search first, then what to show, department,
            category and attributes — one labelled line each, a fine rule between them. A house
            suggestion has none (`filtroShellDisplay`): it is a curation to read through.
          */}
          {isSuggestion ? null : (
            <div className="mt-[clamp(2px,0.8vw,10px)] flex flex-col border-t border-[var(--tb-hairline)]">
              <CatalogSearchField
                key={searchResetKey}
                applied={appliedSearch}
                onApply={applySearch}
                resultCount={isServerCatalog ? catalogRequest.page.totalItems : visible.length}
                placeholder={searchPlaceholder}
              />

              {/*
                "O que exibir" sorts the host's own pieces from the rest, which only means something
                where the whole set is in hand. Over the served catalogue the server cannot count
                what is on this list.
              */}
              {isAddScope && !isServerCatalog ? (
                <AddFilterPills
                  options={options}
                  value={addFilter}
                  onChange={(next) => {
                    setRequestedAddFilter(next);
                    setTaxonomy(EMPTY_TAXONOMY);
                    setSelection(new Set());
                  }}
                />
              ) : null}

              <TaxonomyRail
                rails={trail}
                onSelectType={(value) => {
                  // Handoff §2: "Trocar tipo" zeroes the open facet and the marks — and not the
                  // department or the category it lives in.
                  setTaxonomy((current) => selectType(current, value === "" ? null : value));
                  setFacets(EMPTY_FACET_FILTERS);
                  leaveSlice();
                  pulseSkeleton();
                }}
                // Descending a level clears the one below, and changing slice closes the facets: a
                // collection mark absent from the new grouping must not survive the navigation.
                onSelectMacro={(value) => {
                  setTaxonomy(selectMacro(value === "" ? null : (value as MacroKey)));
                  // Handoff §2: "Trocar departamento" zeroes marks, featured and the page — and,
                  // by the product owner's wider rule, the search (see `clearRefinements`).
                  clearRefinements();
                  pulseSkeleton();
                }}
                onSelectCategory={(value) => {
                  setTaxonomy((current) => selectCategory(current, value === "" ? null : value));
                  // The handoff's reset table does not list the page for a category, and in hand
                  // there is no page to reset. Over the server there is: page 3 of the whole
                  // catalogue does not exist in a category of ten, so the page goes back to 1.
                  // The search goes too (see `clearRefinements`).
                  clearRefinements();
                  pulseSkeleton();
                }}
              />

              <FacetRail
                scoped={isServerCatalog ? shown : rails.scoped}
                filters={facets}
                onFiltersChange={changeFilters}
                visibleCount={isServerCatalog ? catalogRequest.page.totalItems : shownCount}
                isCountPending={isCountPending}
                featuredCount={featuredCount(rails.base)}
                serverFacets={isServerCatalog ? railFacets : undefined}
                hasAppliedSearch={appliedSearch !== ""}
                audience={orderAudience}
                isCollectionsDepartment={rails.selection.macro === "colecoes"}
                // One path for both scopes: marks, featured, price, the applied and the typed
                // search, and the page back to 1.
                onClearFilters={clearRecorte}
                onSheetOpenChange={setIsFacetSheetOpen}
              />
            </div>
          )}
        </div>

        <CatalogToolbar
          isCounterPending={isCountPending}
          counter={
            isServerCatalog
              ? catalogCounter({
                  kind: "paged",
                  // Grown by "Carregar mais", the grid holds every page from the first.
                  page: isStacking ? 1 : catalogRequest.page.page,
                  pageSize,
                  shown: shownCount,
                  total: catalogRequest.page.totalItems,
                })
              : catalogCounter({ kind: "list", shown: shownCount, total: scopeTotal })
          }
          order={order}
          orders={catalogOrdersFor(orderAudience)}
          onOrderChange={changeOrder}
        />

        <div className="px-[clamp(20px,4vw,56px)]">
          {/* §8: the wait is declared — a new slice replaces the grid, and a silent replacement
              reads as the screen freezing. */}
          {(isServerCatalog && catalogRequest.isLoading && !isStacking) || (!isServerCatalog && isPulsing) ? (
            <CatalogGridSkeleton />
          ) : isServerCatalog && catalogRequest.isError ? (
            <CatalogError onRetry={catalogRequest.retry} />
          ) : shown.length > 0 ? (
            /*
             * The reveal sits on the grid rather than the `<section>`: the bulk bar and the mode
             * control are fixed and siblings of it, and a `transform` on an ancestor would swap
             * their anchor from the viewport to the element.
             */
            <div
              data-fade="1"
              className="tb-catalogo-grid mt-[clamp(24px,3vw,40px)]"
            >
              {shown.map((piece) => (
                <motion.div
                  layout="position"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: "spring", bounce: 0.1, duration: 0.5 }}
                  key={piece.id}
                  // The grid stretches each item to its row; the card fills it, so its action sits
                  // at the foot and a row's actions line up, as the prototype's cards do (the card
                  // is the grid item there, `display:flex; flex-direction:column`).
                  className="flex flex-col"
                >
                  <PieceCard
                    piece={piece}
                    href={resolvePieceHref(piece)}
                    onOpen={
                      isGuestPage
                        ? () =>
                            track({
                              event: "select_item",
                              item_list_name: "catalog_guest",
                              position: shown.indexOf(piece) + 1,
                              is_highlighted: piece.featured,
                              ...listIdParam,
                              ecommerce: {
                                currency: "BRL",
                                items: [pieceToAnalyticsItem(piece, { index: shown.indexOf(piece), listName: "catalog_guest" })],
                              },
                            })
                        : undefined
                    }
                    onOpenPack={
                      canWrite && !isCuration
                        ? (selected, trigger) => {
                            packTrigger.current = trigger;
                            setPackPiece(selected);
                          }
                        : undefined
                    }
                    selection={
                      isCuration
                        ? undefined
                        : isAddScope
                          ? {
                              isSelected: selection.has(piece.id),
                              onToggle: toggleSelection,
                            }
                          : undefined
                    }
                    curation={
                      isCuration ? { onToggle: toggleInCuration } : undefined
                    }
                    featuring={
                      isAddScope || !canWrite
                        ? undefined
                        : {
                            onToggle: (selected) => {
                              const result = toggleFeatured(withShown(selected), selected.id);
                              // Only a star that was put on and held; at the limit it is refused.
                              if (!selected.featured && !result.isRejected) {
                                track(
                                  itemHighlighted(
                                    selected.id,
                                    result.pieces.filter((piece) => piece.featured).length,
                                    FEATURED_LIMIT,
                                  ),
                                );
                              }
                              commit(result);
                            },
                          }
                    }
                    isSuggestion={isSuggestion}
                    guestPresenting={
                      isGuestPreview && !isSuggestion
                        ? {
                            onPresent: presentAsGuest,
                            isClosed,
                            isBlocked: isOwnList,
                            isAdding: presentingPieceId === piece.id,
                            isAnyAdding: isPresentingAny,
                          }
                        : undefined
                    }
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <CatalogEmpty
              scope={scope}
              // The prototype's `vazioVM` has host copy ("A sua lista ainda não tem peças") only
              // for `gestao`; every other context, the guest's included, reads the filter's.
              isFiltering={isFiltering || isReadOnlyPage}
              onGoToScope={changeScope}
              onClearAll={clearRecorte}
            />
          )}

          {/* §7: "Página X de N" com quatro controles no desktop, "Carregar mais" no celular.
              Hidden after a failed read: there is no page count to state, and "Carregar mais"
              would offer more of what could not be loaded. */}
          {isServerCatalog && !catalogRequest.isError ? (
            <CatalogPagination
              page={catalogRequest.page.page}
              totalPages={catalogRequest.page.totalPages}
              shown={shownCount}
              totalItems={catalogRequest.page.totalItems}
              isCountPending={isCountPending}
              pageSize={pageSize}
              onPageChange={(next) => {
                setIsStacking(false);
                setPage(next);
                // Another page is another set of pieces (see `leaveSlice`).
                setSelection(new Set());
              }}
              onPageSizeChange={(next) => {
                setPageSize(next);
                leaveSlice();
              }}
              // Neither clears nor restarts: it concatenates, nothing leaves the grid.
              onLoadMore={() => {
                setIsStacking(true);
                setPage((current) => current + 1);
              }}
              isLoading={
                catalogRequest.isLoading || catalogRequest.isRefreshing
              }
            />
          ) : null}
        </div>
      </div>

      {isAddScope && isPanelTab ? (
        <BulkBar
          pieces={withShown(...shown)}
          selection={selection}
          onApply={applyBulkAction}
          onClear={() => setSelection(new Set())}
        />
      ) : null}

      <CatalogBrandQuote />

      {/*
        The suggestion's invitation (the prototype's `barraDisplay`): sticky at the foot of the
        window while the list is on screen. "Quero essa lista" starts the person's own list.
      */}
      {isSuggestion && suggestion !== undefined ? (
        <div className="sticky bottom-0 z-40 mt-[clamp(34px,4vw,52px)] flex w-screen transform-gpu flex-wrap items-center justify-between gap-4 border-t border-[var(--tb-hairline)] bg-[rgb(251_247_244_/_0.94)] px-[clamp(20px,4vw,56px)] py-4 backdrop-blur-[14px] [margin-inline:calc(50%-50vw)]">
          <span className="flex min-w-0 flex-col gap-[3px]">
            <span className="text-[14px] leading-[1.4] text-[var(--fg)]">
              Gostou desta curadoria?
            </span>
            <span className="text-[12.5px] leading-[1.4] text-[var(--fg-muted)]">
              A sua lista começa com estas peças, e você ajusta o que quiser
              depois.
            </span>
          </span>
          <AdoptSuggestionLink
            href={suggestion.adoptHref}
            onAdopt={() => {
              completeOnboardingStep("suggestion_preview");
              if (suggestion.analytics === undefined) return;

              const { suggestionId, itemsCount } = suggestion.analytics;
              void loadAnalyticsVisitor().then((visitor) =>
                track({
                  event: "suggestion_list_adopted",
                  suggestion_id: suggestionId,
                  items_count: itemsCount,
                  was_logged_in: visitor.kind !== "visitor",
                }),
              );
            }}
            className="inline-flex min-h-[var(--tb-touch-min)] items-center justify-center whitespace-nowrap bg-tb-green px-6 py-3.5 text-[12px] font-medium uppercase leading-[normal] tracking-[0.14em] text-[var(--tb-porcelain)] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-green-deep)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2"
          >
            {suggestion.adoptLabel ?? SUGGESTION_ADOPT_LABEL}
          </AdoptSuggestionLink>
        </div>
      ) : null}

      {/* The curation's count and the way on (`curBarraDisplay`), sticky at the window's foot. */}
      {isCuration ? (
        <div className="sticky bottom-0 z-40 mt-[clamp(34px,4vw,52px)] flex w-screen transform-gpu flex-wrap items-center justify-between gap-4 border-t border-[var(--tb-border)] bg-[rgb(251_247_244_/_0.94)] px-[clamp(20px,4vw,56px)] py-4 backdrop-blur-[14px] [margin-inline:calc(50%-50vw)]">
          <span className="flex min-w-0 flex-col gap-[3px]">
            <span
              aria-live="polite"
              className="text-[14px] leading-[1.4] text-[var(--fg)]"
            >
              {curationCountLabel(chosenCount)}
            </span>
            <span className="text-[12.5px] leading-[1.4] text-[var(--fg-muted)]">
              {chosenCount === 0
                ? "Escolha ao menos uma peça para continuar."
                : "Você ajusta quantidades e pacotes depois, na sua lista."}
            </span>
          </span>
          <button
            type="button"
            onClick={() => void finishCuration()}
            /*
              Still pressable while a write travels: the press joins the write chain and leaves
              when its turn comes (`finishCuration`). Disabling it here instead would make the
              way out of the step depend on how fast the network is. `aria-busy` is what reports
              the wait, and a second departure is held off by `isHandingOver`.
            */
            disabled={isFinishingCuration}
            aria-busy={isSaving || isFinishingCuration}
            className="inline-flex min-h-[var(--tb-touch-min)] cursor-pointer items-center justify-center whitespace-nowrap bg-tb-green px-6 py-3.5 text-[12px] font-medium uppercase leading-[normal] tracking-[0.16em] text-tb-white transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:enabled:bg-[var(--tb-green-deep)] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2"
          >
            {/*
              "Salvar e ir para o meu painel", as §5.9 names the way out of the curation — it
              said "Continuar", which promised another step where the panel was the destination.
            */}
            {chosenCount === 0
              ? "Escolher peças"
              : isFinishingCuration
                ? "Aguarde..."
                : "Salvar e ir para o meu painel"}
          </button>
        </div>
      ) : null}

      {/* The floating mode control is the host's: a guest has no mode to leave and no scope to add. */}
      {!isPanelTab ? null : (
        <ModeFob
          scope={scope}
          enabled={!isFacetSheetOpen}
          counts={scopeCountsForFob}
          onChange={changeViewFromFob}
          isGuestPreview={isGuestPreview}
          onToggleGuestPreview={toggleGuestPreviewFromFob}
          // Nothing to add to a list that cannot be changed; sharing and previewing stay.
          canAddPieces={canWrite}
          publicListUrl={publicListUrl}
        />
      )}

      {/* Stacked layer, outside the initial bundle (criterion 23.6). */}
      {packPiece !== null ? (
        <PackDialogLazy
          piece={packPiece}
          onClose={() => setPackPiece(null)}
          onSave={savePack}
          onRemove={removeFromList}
          returnFocusTo={packTrigger}
        />
      ) : null}
    </section>
  );
}
